import { useState, Fragment } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Plus, Search, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react'
import { products } from '@/lib/api'
import { formatPrice } from '@/lib/utils'
import ProductStatsPanel from '@/components/ProductStatsPanel'

export default function ProductsPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null)
  const pageSize = 20

  const productsQuery = useQuery({
    queryKey: ['products', page, search],
    queryFn: () => products.list({ page, page_size: pageSize, search: search || undefined }),
  })

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
          Productos
        </h1>
        <div className="flex gap-2">
          <Link to="/products/create" className="btn btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Crear productos con IA
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o referencia..."
              className="input pl-10"
            />
          </div>
          <button
            onClick={() => productsQuery.refetch()}
            className="btn btn-secondary"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </button>
        </div>
      </div>

      {/* Products Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-cell font-semibold text-gray-700 dark:text-gray-200 w-10"></th>
                <th className="table-cell font-semibold text-gray-700 dark:text-gray-200">Código</th>
                <th className="table-cell font-semibold text-gray-700 dark:text-gray-200">Referencia</th>
                <th className="table-cell font-semibold text-gray-700 dark:text-gray-200">Descripción</th>
                <th className="table-cell font-semibold text-gray-700 dark:text-gray-200 text-right">P. Coste</th>
                <th className="table-cell font-semibold text-gray-700 dark:text-gray-200 text-right">P. Venta</th>
                <th className="table-cell font-semibold text-gray-700 dark:text-gray-200">Familia</th>
                <th className="table-cell font-semibold text-gray-700 dark:text-gray-200">EAN</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {productsQuery.isLoading ? (
                <tr>
                  <td colSpan={8} className="table-cell text-center py-8 text-slate-600 dark:text-slate-400">
                    Cargando productos...
                  </td>
                </tr>
              ) : productsQuery.isError ? (
                <tr>
                  <td colSpan={8} className="table-cell text-center py-8 text-danger-600 dark:text-danger-400">
                    Error cargando productos
                  </td>
                </tr>
              ) : productsQuery.data?.products.length === 0 ? (
                <tr>
                  <td colSpan={8} className="table-cell text-center py-8 text-slate-500 dark:text-slate-400">
                    No se encontraron productos
                  </td>
                </tr>
              ) : (
                productsQuery.data?.products.map((product) => {
                  const isExpanded = expandedProduct === product.reference
                  return (
                    <Fragment key={product.id || product.code}>
                      <tr
                        className="table-row cursor-pointer"
                        onClick={() => setExpandedProduct(isExpanded ? null : product.reference)}
                      >
                        <td className="table-cell">
                          <button
                            className="p-1 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
                            title="Ver estadisticas Factusol"
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-primary-600" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                            )}
                          </button>
                        </td>
                        <td className="table-cell font-mono text-sm text-slate-600 dark:text-slate-400">
                          {product.code || '-'}
                        </td>
                        <td className="table-cell font-medium text-primary-600 dark:text-primary-400">
                          {product.reference || '-'}
                        </td>
                        <td className="table-cell max-w-md">
                          <span className="line-clamp-2 text-gray-900 dark:text-gray-100" title={typeof product.name === 'string' ? product.name : ''}>
                            {typeof product.name === 'string' ? product.name : (product.name?.['1'] || Object.values(product.name || {})[0] || 'Sin nombre')}
                          </span>
                        </td>
                        <td className="table-cell text-right font-mono text-slate-700 dark:text-slate-300">
                          {formatPrice(product.price)}
                        </td>
                        <td className="table-cell text-right font-mono">
                          {product.sale_price ? (
                            <span className="text-success-600 dark:text-success-400 font-semibold">
                              {formatPrice(product.sale_price)}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="table-cell">
                          {product.family_id ? (
                            <span className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded">
                              {product.family_id}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="table-cell font-mono text-xs text-slate-500 dark:text-slate-400">
                          {product.ean13 || '-'}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={8} className="p-0 bg-slate-50 dark:bg-slate-900">
                            <div className="p-4">
                              <ProductStatsPanel
                                reference={product.reference}
                                className="shadow-none border-0"
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {productsQuery.data && productsQuery.data.total > pageSize && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-700">
            <div className="text-sm text-slate-700 dark:text-slate-300">
              Mostrando {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, productsQuery.data.total)} de {productsQuery.data.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="btn btn-secondary"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page * pageSize >= productsQuery.data.total}
                className="btn btn-secondary"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
